import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosClient from "../api/axiosClient";

export interface ReportTransaction {
  booking_id: string;
  start_time: string;
  client_name: string;
  service: string;
  staff_id: string;
  staff_name: string;
  price: number;
}

export interface StaffPerformance {
  staff_id: string;
  staff_name: string;
  total_appointments: number;
  total_earnings: number;
  avg_per_service: number;
}

export interface BusiestPeriod {
  label: string;
  count: number;
  earnings: number;
}

export interface ReportData {
  transactions: ReportTransaction[];
  staff_performance: StaffPerformance[];
  busiest_periods: {
    day: BusiestPeriod;
    week: BusiestPeriod;
    month: BusiestPeriod;
    year: BusiestPeriod;
  };
}

interface ReportsState {
  data: ReportData | null;
  loading: boolean;
  error: string | null;
}

const initialState: ReportsState = {
  data: null,
  loading: false,
  error: null,
};

function authHeader(): Record<string, string> {
  const token = window.localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const fetchReport = createAsyncThunk(
  "reports/fetchReport",
  async (
    { businessId, from, to }: { businessId: string; from: string; to: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await axiosClient.get("/reports", {
        params: { businessId, from, to },
        headers: authHeader(),
      });
      return res.data as ReportData;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch report");
    }
  }
);

const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReport.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default reportsSlice.reducer;
